import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  HbrCard,
  HbrOption,
  HbrSelect,
  HbrTag,
} from '@harbor/elements-react-standalone';
import {
  useFetchOpportunity,
  useFetchOpportunityProducts,
} from '../../../../hooks/opportunity-hooks';
import AIContent from '../../../Common/AIContent';
import ResourceDrillModal from '../../../Common/ResourceDrillModal';
import StbButton from '../../../security-toolbox-ui/StbButton';
import StbLoader from '../../../security-toolbox-ui/StbLoader';
import StbMarkdown from '../../../security-toolbox-ui/StbMarkdown';
import StbAccordion from '../../../security-toolbox-ui/StbAccordion';
import OpportunityPhaseLayout from '../OpportunityPhaseLayout';
import OpportunityPhaseCard from '../OpportunityPhaseCard';
import {
  bucketOrder,
  BucketKey,
  DashboardEntity,
  DashboardFeature,
  DashboardProduct,
  getProductCapabilities,
  getProductDashboardModel,
} from './presentProductHelpers';
import './present-phase.css';
import { postItem } from '../../../../support/queries';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../support/store';

type SectionKey = 'material' | 'ai';

type ProductEntry = {
  raw: any;
  dashboard: DashboardProduct;
};

type DrillState = {
  open: boolean;
  entity: DashboardEntity | null;
  activeBucket: BucketKey;
};

const PRESENT_PHASE_QUESTION_ID = 105;
const FEATURE_PREVIEW_MAX_HEIGHT = 220;

const SummaryCard: React.FC<{
  label: string;
  value: React.ReactNode;
}> = ({ label, value }) => (
  <HbrCard className="present-summary-card">
    <div className="present-card-inner present-summary-card__inner">
      <div className="present-summary-card__label">{label}</div>
      <div className="present-summary-card__value">{value}</div>
    </div>
  </HbrCard>
);

const ColoredTag: React.FC<{
  children: React.ReactNode;
  tone?: 'vendor' | 'architecture' | 'neutral' | 'offer-type';
  className?: string;
}> = ({ children, tone = 'neutral', className = '' }) => {
  return (
    <span className={`present-chip-shell present-chip-shell--${tone} ${className}`}>
      <span className="present-chip-text">{children}</span>
    </span>
  );
};

const GroupCapabilityTagList: React.FC<{
  group: DashboardEntity;
  onOpen: (entity: DashboardEntity, bucket?: BucketKey) => void;
}> = ({ group, onOpen }) => {
  const availableBuckets = bucketOrder.filter((bucket) => !!group.capabilities?.[bucket]);

  if (!availableBuckets.length) return null;

  return (
    <div className="present-static-bucket-tags">
      {availableBuckets.map((bucket) => (
        <button
          key={`${group.id ?? group.name}-${bucket}`}
          type="button"
          className="present-feature-bucket-button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onOpen(group, bucket);
          }}
        >
          <HbrTag>{bucket}</HbrTag>
        </button>
      ))}
    </div>
  );
};

const ProductCapabilitySummary: React.FC<{
  product: DashboardProduct;
  onOpen: (entity: DashboardEntity, bucket?: BucketKey) => void;
}> = ({ product, onOpen }) => {
  const hasAnyGuidance = bucketOrder.some((bucket) => !!product.capabilities?.[bucket]);
  const hasAnyExternalLinks = !!product.externalLinks?.length;

  if (!hasAnyGuidance && !hasAnyExternalLinks) return null;

  return (
    <div className="present-product-capabilities">
      <div className="present-section-mini-title">Available Guidance</div>

      {hasAnyGuidance ? (
        <GroupCapabilityTagList group={product} onOpen={onOpen} />
      ) : null}

      {hasAnyExternalLinks ? (
        <div className="present-static-bucket-tags">
          {product.externalLinks.map((link) => (
            <a
              key={`${link.label}-${link.url}`}
              href={link.url}
              target="_blank"
              rel="noreferrer"
              className="present-external-link-chip"
            >
              <ColoredTag tone="neutral">{link.label}</ColoredTag>
            </a>
          ))}
        </div>
      ) : null}
    </div>
  );
};

const CompactRelatedFeatureTags: React.FC<{
  features: DashboardFeature[];
  onOpen: (entity: DashboardEntity, bucket?: BucketKey) => void;
}> = ({ features, onOpen }) => {
  if (!features.length) {
    return (
      <div className="present-empty-copy">
        <em>No related features</em>
      </div>
    );
  }

  return (
    <div
      className="present-related-list-scroll"
      style={{ maxHeight: `${FEATURE_PREVIEW_MAX_HEIGHT}px` }}
    >
      <div className="present-compact-feature-tag-list">
        {features.map((feature) => (
          <button
            key={feature.id ?? feature.name}
            type="button"
            className="present-inline-feature-button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onOpen(feature, 'What');
            }}
            title={feature.description || feature.name}
          >
            <HbrTag>{feature.name}</HbrTag>
          </button>
        ))}
      </div>
    </div>
  );
};

const UseCasesAccordion: React.FC<{
  product: DashboardProduct;
  onOpen: (entity: DashboardEntity, bucket?: BucketKey) => void;
}> = ({ product, onOpen }) => {
  const groups = (product.useCaseGroups || []).filter(
    (group) => (group.features?.length || 0) > 0 || bucketOrder.some((bucket) => !!group.capabilities?.[bucket])
  );

  if (!groups.length) {
    return (
      <div className="present-empty-copy">
        <em>No use cases found</em>
      </div>
    );
  }

  return (
    <div className="present-accordion-list">
      {groups.map((group) => (
        <StbAccordion
          key={group.id ?? group.name}
          variant="stacked"
          label={group.name}
          className="present-accordion-item"
        >
          <div className="present-accordion-body">
            {group.description ? (
              <div className="present-accordion-description">
                <StbMarkdown>{group.description}</StbMarkdown>
              </div>
            ) : null}

            <GroupCapabilityTagList group={group} onOpen={onOpen} />

            <div className="present-accordion-divider" />

            <div className="present-accordion-section-block">
              <div className="present-accordion-subtitle">
                Related Features ({group.features.length})
              </div>
              <CompactRelatedFeatureTags features={group.features} onOpen={onOpen} />
            </div>
          </div>
        </StbAccordion>
      ))}
    </div>
  );
};

const ComponentsAccordion: React.FC<{
  product: DashboardProduct;
  onOpen: (entity: DashboardEntity, bucket?: BucketKey) => void;
}> = ({ product, onOpen }) => {
  const groups = (product.componentGroups || []).filter(
    (group) => (group.features?.length || 0) > 0 || bucketOrder.some((bucket) => !!group.capabilities?.[bucket])
  );

  if (!groups.length) {
    return (
      <div className="present-empty-copy">
        <em>No components found</em>
      </div>
    );
  }

  return (
    <div className="present-accordion-list">
      {groups.map((group) => (
        <StbAccordion
          key={group.id ?? group.name}
          variant="stacked"
          label={group.name}
          className="present-accordion-item"
        >
          <div className="present-accordion-body">
            {group.description ? (
              <div className="present-accordion-description">
                <StbMarkdown>{group.description}</StbMarkdown>
              </div>
            ) : null}

            <GroupCapabilityTagList group={group} onOpen={onOpen} />

            <div className="present-accordion-divider" />

            <div className="present-accordion-section-block">
              <div className="present-accordion-subtitle">
                Related Features ({group.features.length})
              </div>
              <CompactRelatedFeatureTags features={group.features} onOpen={onOpen} />
            </div>
          </div>
        </StbAccordion>
      ))}
    </div>
  );
};

const PresentPhase: React.FC = () => {
  const { id: opportunityId } = useParams();
  const { email } = useSelector((state: RootState) => state.user.userData);
  const { data: opportunity } = useFetchOpportunity(opportunityId, { enabled: !!opportunityId });
  const { data: products, isLoading: productsLoading } = useFetchOpportunityProducts(opportunityId, {
    enabled: !!opportunityId,
  });

  const productEntries = useMemo<ProductEntry[]>(() => {
    return (products || [])
      .filter((product: any) => product?.vendor?.is_cisco === true)
      .map((product: any) => ({
        raw: product,
        dashboard: getProductDashboardModel(product),
      }));
  }, [products]);

  const [selectedSection, setSelectedSection] = useState<SectionKey>('material');
  const [selectedProductId, setSelectedProductId] = useState('');
  const [drillState, setDrillState] = useState<DrillState>({
    open: false,
    entity: null,
    activeBucket: 'What',
  });

  useEffect(() => {
    if (!selectedProductId && productEntries.length) {
      setSelectedProductId(String(productEntries[0].dashboard.id));
    }
  }, [productEntries, selectedProductId]);

  const selectedEntry = useMemo(() => {
    return (
      productEntries.find((entry) => String(entry.dashboard.id) === String(selectedProductId)) || null
    );
  }, [productEntries, selectedProductId]);

  const selectedProduct = selectedEntry?.dashboard || null;
  const selectedRawProduct = selectedEntry?.raw || null;

  useEffect(() => {
    setDrillState({
      open: false,
      entity: null,
      activeBucket: 'What',
    });
  }, [selectedProductId]);

  const visibleUseCaseCount = useMemo(() => {
    return (selectedProduct?.useCaseGroups || []).filter(
      (g) => (g.features?.length || 0) > 0 || bucketOrder.some((bucket) => !!g.capabilities?.[bucket])
    ).length;
  }, [selectedProduct]);

  const visibleComponentCount = useMemo(() => {
    return (selectedProduct?.componentGroups || []).filter(
      (g) => (g.features?.length || 0) > 0 || bucketOrder.some((bucket) => !!g.capabilities?.[bucket])
    ).length;
  }, [selectedProduct]);

  const openDrillIn = (entity: DashboardEntity, bucket: BucketKey = 'What') => {
    setDrillState({
      open: true,
      entity,
      activeBucket: bucket,
    });
  };

  const closeDrillIn = async (): Promise<void> => {
    setDrillState({
      open: false,
      entity: null,
      activeBucket: 'What',
    });
  };

  return (
    <OpportunityPhaseLayout
      title="Present Phase"
      subtitle="Explore Cisco product material, resources, use cases, components, and AI-generated presentation guidance."
      actions={
        <div className="present-toolbar-actions">
          <div className="present-toolbar-actions__left">
            <div className="hbr-button-group present-mode-button-group">
              <StbButton
                role="default"
                onClick={() => setSelectedSection('material')}
                className={`hbr-button-group__button hbr-button-group__button--first ${
                  selectedSection === 'material' ? 'present-mode-button--active' : ''
                }`}
              >
                Product Data
              </StbButton>
              <StbButton
                role="request"
                onClick={() => setSelectedSection('ai')}
                className={`hbr-button-group__button hbr-button-group__button--last ${
                  selectedSection === 'ai' ? 'present-mode-button--active' : ''
                }`}
              >
                AI Suggestions
              </StbButton>
            </div>
          </div>

          <div className="present-product-select-wrap">
            <HbrSelect
              value={selectedProductId}
              onHbr-change={(event: any) => setSelectedProductId(event?.target?.value || '')}
              placeholder="Select product"
            >
              {productEntries.map((entry) => (
                <HbrOption key={entry.dashboard.id} value={String(entry.dashboard.id)}>
                  {entry.dashboard.name}
                </HbrOption>
              ))}
            </HbrSelect>
          </div>
        </div>
      }
    >
      <OpportunityPhaseCard
        title={selectedSection === 'ai' ? 'AI Presentation Guide' : selectedProduct?.name ? null : 'Loading'}
      >
        <StbLoader isLoading={productsLoading} />

        {!productsLoading && !selectedProduct && (
          <div>
            <em>No Cisco products found</em>
          </div>
        )}

        {selectedProduct && selectedSection === 'ai' && (
          <div key={`product-ai-${selectedProduct.id}`}>
            <AIContent
              related_type="opportunity"
              related_id={String(opportunityId)}
              question_id={PRESENT_PHASE_QUESTION_ID}
              product_name={selectedProduct.name}
              item={selectedRawProduct}
              height={480}
              width="100%"
              context={selectedProduct.description || ''}
              pdfTitle={`${opportunity?.Name || selectedProduct.name} Present Guide`}
            />
          </div>
        )}

        {selectedProduct && selectedSection === 'material' && (
          <div className="present-dashboard" key={`product-material-${selectedProduct.id}`}>
            <div className="present-hero-grid">
              <div className="present-hero-grid__overview">
                <HbrCard className="present-panel-card present-panel-card--full-height">
                  <div className="present-card-inner present-card-inner--full-height">
                    <div className="present-panel-card__title">Product Overview</div>

                    <div className="present-product-header">
                      <div className="present-product-header__main">
                        <div className="present-product-title-row">
                          <div className="present-product-title">{selectedProduct.name}</div>

                          <div className="present-product-title-tags">
                            {selectedProduct.vendor ? (
                              <ColoredTag tone="vendor">{selectedProduct.vendor}</ColoredTag>
                            ) : null}

                            {selectedProduct.offerType ? (
                              <ColoredTag tone="offer-type" className="present-chip-shell--small">
                                {selectedProduct.offerType}
                              </ColoredTag>
                            ) : null}
                          </div>
                        </div>

                        {selectedProduct.subtitle ? (
                          <div className="present-product-subtitle">{selectedProduct.subtitle}</div>
                        ) : null}
                      </div>
                    </div>

                    <div className="present-product-description">
                      {selectedProduct.description ? (
                        <StbMarkdown>{selectedProduct.description}</StbMarkdown>
                      ) : (
                        <div className="present-empty-copy">No product overview available.</div>
                      )}
                    </div>

                    {!!selectedProduct.architectures?.length && (
                      <div className="present-tag-list present-tag-list--architectures">
                        {selectedProduct.architectures.map((arch, index) => (
                          <ColoredTag key={`${arch}-${index}`} tone="architecture">
                            {arch}
                          </ColoredTag>
                        ))}
                      </div>
                    )}

                    <ProductCapabilitySummary product={selectedProduct} onOpen={openDrillIn} />
                  </div>
                </HbrCard>
              </div>

              <div className="present-hero-grid__stats">
                <div className="present-stats-grid">
                  <SummaryCard label="Versions" value={selectedProduct.versions?.length || 0} />
                  <SummaryCard label="Components" value={visibleComponentCount} />
                  <SummaryCard label="Use Cases" value={visibleUseCaseCount} />
                  <SummaryCard label="Features" value={selectedProduct.features?.length || 0} />
                </div>
              </div>
            </div>

            <HbrCard className="present-panel-card">
              <div className="present-card-inner">
                <div className="present-panel-card__title">Versions & Resources</div>

                {selectedProduct.versions?.length ? (
                  <div className="present-version-grid">
                    {selectedProduct.versions.map((version) => (
                      <div
                        key={version.id ?? version.name}
                        className="present-version-resource-card"
                      >
                        <div className="present-version-resource-card__title">{version.name}</div>

                        <div className="present-version-resource-card__meta">
                          {version.released ? (
                            <div>
                              <strong>Released:</strong> {version.released}
                            </div>
                          ) : null}
                          {version.lastUpdate ? (
                            <div>
                              <strong>Last Update:</strong> {version.lastUpdate}
                            </div>
                          ) : null}
                          {version.endOfLife ? (
                            <div>
                              <strong>End of Life:</strong> {version.endOfLife}
                            </div>
                          ) : null}
                        </div>

                        {version.description ? (
                          <div className="present-version-resource-card__description">
                            <StbMarkdown>{version.description}</StbMarkdown>
                          </div>
                        ) : null}

                        {version.links?.length ? (
                          <div className="present-links">
                            {version.links.map((link, linkIndex) => (
                              <a
                                key={`${version.name}-${link.url}-${linkIndex}`}
                                href={link.url}
                                target="_blank"
                                rel="noreferrer"
                                className="present-link"
                              >
                                {link.label}
                              </a>
                            ))}
                          </div>
                        ) : (
                          <div className="present-empty-copy">No resources available.</div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="present-empty-copy">No version resource data available.</div>
                )}
              </div>
            </HbrCard>

            <div className="present-middle-row present-middle-row--two">
              <div className="present-middle-row__col">
                <HbrCard className="present-panel-card">
                  <div className="present-card-inner">
                    <div className="present-panel-card__title">Use Cases</div>
                    <UseCasesAccordion product={selectedProduct} onOpen={openDrillIn} />
                  </div>
                </HbrCard>
              </div>

              <div className="present-middle-row__col">
                <HbrCard className="present-panel-card">
                  <div className="present-card-inner">
                    <div className="present-panel-card__title">Components</div>
                    <ComponentsAccordion product={selectedProduct} onOpen={openDrillIn} />
                  </div>
                </HbrCard>
              </div>
            </div>
          </div>
        )}

        <ResourceDrillModal
          open={drillState.open}
          entity={drillState.entity}
          activeBucket={drillState.activeBucket}
          bucketOrder={bucketOrder}
          onBucketChange={(bucket) =>
            setDrillState((prev) => ({
              ...prev,
              activeBucket: bucket,
            }))
          }
          onClose={closeDrillIn}
          title={selectedProduct?.name || 'Detail'}
          userEmail={email}
          productId={selectedProduct?.id}
          productName={selectedProduct?.name}
        />
      </OpportunityPhaseCard>
    </OpportunityPhaseLayout>
  );
};

export default PresentPhase;